import type { ReimbursementExtendedFields } from '../types/reimbursementFormTypes'
import { EMPTY_REIMBURSEMENT_EXTENDED_FIELDS } from '../types/reimbursementFormTypes'

/** BE stores only `unused_security_deposit`; null/empty amount means deposit was not collected. */
function inferSecurityDepositCollectedFromAmount(raw: unknown): boolean {
  if (raw === null || raw === undefined || String(raw).trim() === '') {
    return false
  }
  const cleaned = String(raw).replace(/[^0-9.]/g, '')
  if (!cleaned) return false
  const num = Number(cleaned)
  if (!Number.isFinite(num)) return false
  return num > 0
}

function apiYesNoToBoolean(raw: unknown): boolean | null {
  if (raw === null || raw === undefined || raw === '') return null
  if (raw === false || raw === 0 || raw === '0') return false
  if (typeof raw === 'string' && ['no', 'false'].includes(raw.toLowerCase())) return false
  return true
}

/**
 * Yes/No is derived from `unused_security_deposit` on GET. Until a positive amount is saved,
 * the API often returns null, empty, or `$0.00` — preserve local choice across reloads.
 */
function mergeSecurityDepositCollected(
  residentDefault: Record<string, unknown> | null | undefined,
  fromApi: boolean | null,
  prev: boolean | null,
): boolean | null {
  const hasPersistedPositiveAmount = inferSecurityDepositCollectedFromAmount(
    residentDefault?.unused_security_deposit,
  )
  if (!hasPersistedPositiveAmount) {
    return prev
  }
  return fromApi
}

export function hydrateReimbursementExtendedFields(
  residentDefault: Record<string, unknown> | null | undefined,
): ReimbursementExtendedFields {
  if (!residentDefault) {
    return { ...EMPTY_REIMBURSEMENT_EXTENDED_FIELDS }
  }

  return {
    lease_modifications_at_move_in: apiYesNoToBoolean(residentDefault.unit_transfer_after_movein),
    security_deposit_collected: inferSecurityDepositCollectedFromAmount(
      residentDefault.unused_security_deposit,
    ),
    cares_act_applicable: apiYesNoToBoolean(residentDefault.notice_required),
    skipped_during_eviction: apiYesNoToBoolean(
      residentDefault.vacate_before_eviction_lawsuit_filed,
    ),
    alternate_court_document_applicable: apiYesNoToBoolean(
      residentDefault.alternate_court_issued_document,
    ),
    using_third_party_eviction_services: apiYesNoToBoolean(
      residentDefault.using_third_party_eviction_services,
    ),
    utilizing_legal_team_for_eviction: apiYesNoToBoolean(
      residentDefault.utilizing_legal_team_for_eviction,
    ),
    third_party_servicer_name:
      residentDefault.third_party_servicer_name == null
        ? ''
        : String(residentDefault.third_party_servicer_name),
    bankruptcy_applicable: apiYesNoToBoolean(residentDefault.bankruptcy_applicable),
    appeal_applicable: apiYesNoToBoolean(residentDefault.appeal_applicable),
    resident_paid_into_court: apiYesNoToBoolean(residentDefault.resident_paid_into_court),
    stipulation_applicable: apiYesNoToBoolean(residentDefault.stipulation_applicable),
    resident_defaulted_on_repayment: apiYesNoToBoolean(
      residentDefault.resident_defaulted_on_repayment,
    ),
    payment_info_confirmed: false,
  }
}

function mergeNullableBoolean(api: boolean | null, prev: boolean | null): boolean | null {
  return api !== null ? api : prev
}

/**
 * After document upload the detail GET may return null scalars that were never saved.
 * Preserve in-progress answers from local state when the API has no value yet.
 */
export function mergeReimbursementExtendedFields(
  residentDefault: Record<string, unknown> | null | undefined,
  prev: ReimbursementExtendedFields,
): ReimbursementExtendedFields {
  const fromApi = hydrateReimbursementExtendedFields(residentDefault)

  return {
    ...fromApi,
    lease_modifications_at_move_in: mergeNullableBoolean(
      fromApi.lease_modifications_at_move_in,
      prev.lease_modifications_at_move_in,
    ),
    security_deposit_collected: mergeSecurityDepositCollected(
      residentDefault,
      fromApi.security_deposit_collected,
      prev.security_deposit_collected,
    ),
    cares_act_applicable: mergeNullableBoolean(
      fromApi.cares_act_applicable,
      prev.cares_act_applicable,
    ),
    skipped_during_eviction: mergeNullableBoolean(
      fromApi.skipped_during_eviction,
      prev.skipped_during_eviction,
    ),
    alternate_court_document_applicable: mergeNullableBoolean(
      fromApi.alternate_court_document_applicable,
      prev.alternate_court_document_applicable,
    ),
    using_third_party_eviction_services: mergeNullableBoolean(
      fromApi.using_third_party_eviction_services,
      prev.using_third_party_eviction_services,
    ),
    utilizing_legal_team_for_eviction: mergeNullableBoolean(
      fromApi.utilizing_legal_team_for_eviction,
      prev.utilizing_legal_team_for_eviction,
    ),
    third_party_servicer_name:
      fromApi.third_party_servicer_name.trim() !== ''
        ? fromApi.third_party_servicer_name
        : prev.third_party_servicer_name,
    bankruptcy_applicable: mergeNullableBoolean(
      fromApi.bankruptcy_applicable,
      prev.bankruptcy_applicable,
    ),
    appeal_applicable: mergeNullableBoolean(
      fromApi.appeal_applicable,
      prev.appeal_applicable,
    ),
    resident_paid_into_court: mergeNullableBoolean(
      fromApi.resident_paid_into_court,
      prev.resident_paid_into_court,
    ),
    stipulation_applicable: mergeNullableBoolean(
      fromApi.stipulation_applicable,
      prev.stipulation_applicable,
    ),
    resident_defaulted_on_repayment: mergeNullableBoolean(
      fromApi.resident_defaulted_on_repayment,
      prev.resident_defaulted_on_repayment,
    ),
    payment_info_confirmed: prev.payment_info_confirmed,
  }
}
